/** Type file for types exclusively related to the PIM. */
import { StatusColor } from "../components/Status/Status";
import { APIStatusVariant } from "../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import {
  AddressMode,
  AddressValue,
} from "../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import type {
  AdvancedFiltersAppliedCard,
  CurrencyCode,
  Identifiers,
  Permission,
  ProductSelectorCSVDocument,
  ProductSKU,
  SupportedLanguage,
  TDSDocument,
  TeamPermissionsEnum,
  TranslationsSchema,
  UUID,
  WithPagination,
  IPriceTier,
  User,
} from "./types";

/**
 * The naming is confusing here, and this is not a named schema on the backend.
 * Its used for a more limited overview of groups on the table page for
 * groups/groups within templates.
 * This type is only used in conjuction with @see TenantAttributeGroupSummary
 */
export type SingleAttributeGroupSummary = {
  id: UUID;
  attribute_count: number;
  name: string;
  description: string | null;
  display_name: string;
  is_system: boolean;
};

/**
 * @see server/src/agilis/docs/odm/TenantAttributeGroupSummarySchema.yaml
 * GET summary of attribute groups for table.
 * TenantAttributeGroupPaginatedOutputSchema
 */
export type TenantAttributeGroupSummary = WithPagination<{
  data: SingleAttributeGroupSummary[];
}>;

/**
 * This is not a named type in the API doc but its used for creating an
 * attribute that is part of a group, kind of an extension of an attribute.
 */
export type SingleAttributeCreation = {
  id: UUID;
  display_order?: number | null;
  is_attribute?: boolean | null; // defaults to true for now, in the future could be a collection.
  display_name?: string | null;
};

/**
 * @see server/src/agilis/docs/odm/AttributeGroupArgsSchema.yaml
 * Creates a new attribute group, with preexisting attributes. Potentially in
 * the future it may be possible to create an empty group without attributes to
 * start with.
 */
export type AttributeGroupArgsSchema = {
  name: string;
  display_name?: string | null;
  description?: string | null;
  attributes: SingleAttributeCreation[] | null;
  display_configuration_by_attribute_id?: Record<string, string>;
};

/**
 * @see server/src/agilis/docs/odm/AttributeGroupAttributePatchSchema.yaml
 */
export type AttributeGroupAttributePatchSchema = {
  display_name: string;
  display_order?: number | null;
};

/**
 * @see server/src/agilis/docs/odm/AttributeGroupSchema.yaml
 * detail view of indidividual attribute group.
 */
export type AttributeGroupSchema = {
  items: SingleAttributeCreation[];
  properties: {
    id: UUID;
    name: string;
  };
  is_system: boolean;
};

/**
 * @see server/src/agilis/docs/odm/AttributeGroupSummaryPatchSchema.yaml
 * Currently for editing the name of a preexisting group. There is no current
 * way to change the display order of an attribute group. Name and display are
 * currently the same.
 */
export type AttributeGroupSummaryPatch = {
  name: string;
  display_name: string;
  discription: string | null;
};

/**
 * @see server/src/agilis/docs/odm/AttributeGroupSummarySchema.yaml
 * This is for an individual group detail page.
 */
export type AttributeGroupSummary = {
  name: string;
  id: UUID;
  attribute_count: number;
  group_type: string;
  display_name: string;
  is_frozen: boolean;
  description: string | null;
  attributes: AttributeSchema[];
  is_system: boolean;
  is_restricted: boolean;
  is_mandatory: boolean;
  has_reference_entity_mapping: boolean;
};

export type ProductReference = {
  id: UUID;
  name: string;
  slug: string;
  product_number: string;
  image_url: string;
  product_description: string;
  cas_number: string;
  brand_name: string;
  chemical_name: string;
  is_accessible: boolean;
  is_visible_on_storefronts: boolean;
  is_viewable?: boolean;
  pim_product_type?: PIMProduct["pim_product_type"];
  status: ProductStatusType;
  visibility_tenants: {
    id: UUID;
  }[];
};

export type ObjectRefPreviewAttribute = {
  label: string;
  value: string;
};

export type ObjectReferenceResult = {
  id: UUID;
  name: string;
  object_type: string;
  object_number?: string | null;
  status?: string | null;
  slug?: string | null;
  image_url?: string | null;
  preview_attributes?: { key: string; value: string }[];
  is_visible_on_storefronts?: boolean;
  is_accessible?: boolean;
  is_viewable?: boolean;
  pim_product_type?: PIMProduct["pim_product_type"];
  visibility_tenants?: { id: string }[];
};

export interface IproductReferenceCollectionItem {
  id: string;
  name: string;
  slug: string;
}

export interface IuserReferenceCollectionItem {
  id: string;
  name: string;
  slug: string;
}

export interface IobjectReferenceCollectionItem {
  id: string;
  name: string;
  slug: string;
  object_type: string;
  target_object_type: string;
}

export type ImageAttributeValue = {
  caption?: string;
  image_dimensions: "small" | "medium" | "large";
  asset_id: string;
};

export type DocumentAttributeValue = {
  asset_id: string;
  display_name?: string;
  is_external?: boolean;
  host_name?: string | null;
  asset_type?: string;
  content_type?: string;
  signed_url?: string;
  preview_url?: string;
  is_downloadable?: boolean;
  visibility?: SupportedAssetAccessibility;
  is_generated?: boolean;
};

// narrow this type based on attributes that are expected to exist
export type AttributeValue = {
  id: UUID;
  priority: number;
  enum_variant: EnumVariantSchema | null;
  attribute_id: UUID;
  value:
    | string
    | boolean
    | null
    | { url: string; display_text: string }
    | IproductReferenceCollectionItem
    | ImageAttributeValue
    | DocumentAttributeValue
    | User
    | AddressValue;
  data_type: AttributeDataType;
  product_ref?: ProductReference;
  cdn_url?: string;
  caption?: string;
  image_dimensions?: ImageAttributeValue["image_dimensions"];
  preview_url?: string;
  signed_url?: string;
  is_downloadable?: boolean;
  visibility?: SupportedAssetAccessibility;
  user?: User;
  status?: {
    color: StatusColor;
    status: string;
  };
  object_ref?: ObjectReferenceResult | null;
  asset_name?: string;
  asset_type?: string;
  content_type?: string;
  host?: { id: string; name: string } | null;
  asset_mode?: Assets["asset_mode"];
  is_generated?: boolean;
};

/**
 * @see /server/src/agilis/models/constant.py AttributeInputType
 * uncomment commented lines when they are supported. In future potentially this
 * could be split into multiple types.
 */
export type AttributeInput =
  | "date"
  | "document"
  | "form_field"
  | "image"
  | "link"
  | "multiline_entry"
  | "multi_select"
  | "numeric"
  | "single_select"
  // | "timestamp"
  | "toggle"
  | "checkbox"
  | "product_reference"
  | "user"
  | "address"
  | "object_reference";

/**
 * @see AttributeDataType
 */
export type AttributeDataType =
  | "text"
  | "numeric"
  | "date"
  // | "timestamp"
  | "boolean"
  | "document"
  // | "url"
  | "enum";

/**
 * @see /server/src/agilis/models/constant/AttributeObjectType
 * These should really be sorted into string/boolean/numeric values
 */
export type AttributeObjectType =
  | "product_name"
  | "brand_name"
  | "product_id"
  | "produced_by"
  | "product_line"
  | "product_description"
  | "chemical_name"
  | "cas_number"
  | "ec_number"
  | "inchi_key"
  | "inci_name"
  | "molecular_formula"
  | "synonyms"
  | "seo_page_title"
  | "seo_meta_title"
  | "seo_meta_keywords"
  | "seo_meta_description"
  | "product_group_1"
  | "product_group_2"
  | "product_group_3"
  | "applications"
  | "value_propositions"
  | "functions"
  | "industries"
  | "market_segments"
  | "sku_id"
  | "sku_status"
  | "source"
  | "region"
  | "packaging_type"
  | "packaging_unit"
  | "packaging_volume"
  | "packaging_description"
  | "property_name"
  | "specification_name"
  | "value"
  | "unit"
  | "is_a_sample"
  | "test_method"
  | "country"
  | "division"
  | "entity"
  | "country_code"
  | "global_family_name"
  | "company_name"
  | "customer_id"
  | "distributor_id"
  | "supplier_id";

/**
 *  @see server/src/agilis/docs/odm/AttributeSchema.yaml
 * Schema for attributes
 */
export type AttributeSchemaBase = {
  is_required: boolean;
  id: UUID;
  name: "display_name" | string;
  display_name: string;
  description: string | null;
  is_multiple_choice?: boolean;
  default_values: null | AttributeValue[];
  choices?: string[];
  // There's an EP that gives all the possible input types: /v2/pim/input-types
  // input_type: AttributeInput;
  values: AttributeValue[];
  is_system: boolean;
  is_master: boolean;
  list_name: string | null;
  list_id: UUID;
  is_filterable: boolean;
  is_filter_eligible?: boolean;
  object_type: AttributeObjectType;
  new_object_type: string;
  is_restricted: boolean;
  is_visible: boolean;
  is_mandatory: boolean;
  is_editable: boolean;
  edit_blocked_reason?: EditBlockedReason | null;
  translations: TranslationsSchema[];
  size: "small" | "medium" | "large";
  icons_enabled: boolean;
  is_not_applicable?: boolean;
  input_type?: AttributeInput;
  address_mode?: AddressMode;
};

/**
 * @see server/src/agilis/docs/external_api.yaml#/components/schemas/EditBlockedReason
 */
export const EDIT_BLOCKED_REASON = {
  ATTRIBUTE_IS_IDENTIFIER: "attribute_is_identifier",
  SCHEMA_LOCKED: "schema_locked",
  MASTER_OWNED_ATTRIBUTE: "master_owned_attribute",
  APPROVAL_IS_ACTIVE: "approval_is_active",
  TEAM_DENIED: "team_denied",
  NOT_IN_VIEW: "not_in_view",
  ROLE_NOT_GRANTED: "role_not_granted",
} as const;

export type EditBlockedReason =
  typeof EDIT_BLOCKED_REASON[keyof typeof EDIT_BLOCKED_REASON];

type StringOrNullValues = {
  input_type: Extract<
    AttributeInput,
    | "date"
    | "form_field"
    | "single_select"
    | "numeric"
    | "multiline_entry"
    | "multi_select"
  >;
};

type ProductReferenceValues = {
  input_type: Extract<AttributeInput, "product_reference">;
  display_configuration?: "link" | "product_card";
};

type BooleanValues = {
  input_type: Extract<AttributeInput, "toggle" | "checkbox">;
};

type ObjectValues = {
  input_type: Extract<AttributeInput, "link" | "image">;
};

type DocumentValues = {
  input_type: Extract<AttributeInput, "document">;
};

type UserValues = {
  input_type: Extract<AttributeInput, "user">;
};

type AddressValues = {
  input_type: Extract<AttributeInput, "address">;
  address_mode?: AddressMode;
};

type ObjectReferenceValues = {
  input_type: Extract<AttributeInput, "object_reference">;
  source_object_type?: string;
  target_object_type?: string;
  target_side_display_name?: string;
  display_configuration?: "link" | "product_card";
  source_preview_attribute_ids?: string[];
  target_preview_attribute_ids?: string[];
  source_preview_attributes?: {
    id: string;
    name: string;
    display_name: string;
    input_type: string;
  }[];
  target_preview_attributes?: {
    id: string;
    name: string;
    display_name: string;
    input_type: string;
  }[];
};

export type AttributeSchema = AttributeSchemaBase &
  (
    | StringOrNullValues
    | ProductReferenceValues
    | BooleanValues
    | ObjectValues
    | DocumentValues
    | UserValues
    | AddressValues
    | ObjectReferenceValues
  );

/**
 * @see EnumVariantSchema
 * currently text is the only possible variant_type. Subject to change in the future
 */
export type EnumVariantSchema = {
  attribute_enum_metadata_id: UUID;
  id: UUID;
  variant: string;
  variant_type: Extract<"text", AttributeDataType>;
  content_type: string | null;
  file_name: string | null;
  thumbnail: string | null; // URL
  badge_color?: StatusColor | null;
};

/**
 * @see server/src/agilis/docs/odm/AttributeArgSchema.yaml
 * request body to create/update an attribute
 */
export type AttributeArgSchema = Omit<AttributeSchema, "id">;

/**
 * @see AttributeSchema, but paginated.
 */
export type PaginatedAttributeSchema = WithPagination<{
  data: AttributeSchema[];
}>;

/**
 * Enum of possible PIM classifications.
 */
export enum PIMAttributesClassification {
  ATTRIBUTES,
  GROUPS,
  COLLECTIONS,
}

export enum PIMLists {
  LISTS,
  ACTIVITY,
}

/**
 * @see server/src/agilis/docs/odm/CollectionColumnCreationSchema.yaml
 */
export type CollectionColumnCreationSchema = {
  attribute_id: UUID;
  display_name?: string;
  is_required: boolean;
  is_editable: boolean;
};

/**
 * @see server/src/agilis/docs/odm/CollectionRowCreationSchema.yaml
 */
export type CollectionRowCreationSchema = {
  attribute_id: UUID;
  default_values?: string[];
};

/**
 * @see server/src/agilis/docs/odm/CollectionSchema.yaml
 */
export type CollectionSchema = {
  attribute_group_id: string;
  id: UUID;
  name: string;
  display_name: string;
  description?: string;
  columns: AttributeSchema[];
  rows: CollectionRowSchema[];
  value_rows?: CollectionValueRowSchema[];
  attribute_count: number;
  is_system: boolean;
  values?: unknown[]; // FIXME
  group_type: string;
  object_type: string;
  is_restricted: boolean;
  is_visible: boolean;
  is_mandatory: boolean;
  has_reference_entity_mapping: boolean;
  enable_collection_filter: boolean;
  enable_collection_search: boolean;
  filterable_column_ids: string[];
};

/**
 * @see server/src/agilis/docs/odm/CollectionPaginatedOutputSchema.yaml
 */
export type CollectionPaginatedOutputSchema = WithPagination<{
  data: CollectionSchema[];
}>;

/**
 * @see server/src/agilis/docs/odm/CollectionCreationSchema.yaml
 */
export type CollectionCreationSchema = {
  parent_group_id?: UUID;
  name: string;
  display_name?: string;
  description?: string;
  columns: CollectionColumnCreationSchema[];
  rows: CollectionRowCreationSchema[][];
};

/**
 * @see server/src/agilis/docs/odm/CollectionPatchArgsSchema.yaml
 */
export type CollectionPatchArgsSchema = CollectionCreationSchema & {
  columns_to_delete?: string[];
  rows_to_delete?: string[];
};

/**
 * @see server/src/agilis/docs/odm/CollectionRowSchema.yaml
 */
export type CollectionRowSchema = {
  id: UUID;
  attributes: AttributeSchema[];
};

export type CollectionValueRowSchema = {
  row_id: UUID;
  values: AttributeValue[];
  is_master: boolean;
  is_editable: boolean;
};

/**
 * @see server/src/agilis/docs/odm/ShortListSchema.yaml
 */
export type ShortListSchema = {
  id: UUID;
  name: string;
  display_name?: string;
  is_system: boolean;
  count: number; // only shows count for active list items
  is_frozen: boolean; // indicates whether it's in use or not
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
  icons_enabled: boolean;
  parent?: {
    id: UUID;
    name: string;
  };
  number_of_matches?: number;
};

export interface DropdownSearchItem {
  id: string;
  is_item_archived: boolean;
  is_list_archived: boolean;
  list_name: string;
  name: string;
}

/**
 * @see server/src/agilis/docs/odm/TenantListPaginatedOutputSchema.yaml
 */
export type PaginatedListSchema = WithPagination<{ data: ShortListSchema[] }>;

export type PaginatedDropdownSearchResults = WithPagination<{
  data: DropdownSearchItem[];
}>;

/**
 * @see server/src/agilis/docs/odm/ListArgSchema.yaml
 */
export type ListPostArgSchema = {
  name: string;
  display_name?: string;
  choices?: string[];
};

export type ListPatchArgSchema = {
  name: string;
  display_name?: string;
  is_deleted: boolean;
};

export type ListItemPatchArgSchema = {
  name: string;
  is_deleted?: boolean;
  icon?: File;
  delete_icon?: boolean;
};

export type ListItemSchema = {
  id: UUID;
  name: string;
  is_frozen: boolean; // indicates if this can be edited or not
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
  thumbnail: string;
  file_name: string;
  has_cdn_image: boolean;
};

export type ListDetailsSchema = {
  id: UUID;
  name: string;
  display_name: string;
  is_frozen: boolean; // indicates if this can be edited or not
  count: number;
  created_at: string;
  created_by: string;
  icons_enabled: boolean;
  is_deleted: boolean;
  is_system: boolean;
  modified_at: string;
  modified_by: string;
  parent: string;
  source: string;
  items: ListItemObjectSchema[];
};

export type ListItemObjectSchema = {
  content_type: string;
  created_at: string;
  created_by: string;
  file_name: string;
  has_cdn_image: boolean;
  id: string;
  is_frozen: boolean;
  modified_at: string;
  modified_by: string;
  name: string;
  thumbnail: string;
};

export type PaginatedListItemSchema = WithPagination<{
  data: ListItemSchema[];
}>;

/**************************
 * TEMPLATE TYPES
 *************************/

/**
 * GET /v2/tenants/{id}/pim/templates
 * Currently no paginated variant of this, just an array.
 * @see /server/src/agilis/docs/odm/AttributeTemplateSchema.yaml
 */
export type TemplateType = "product" | "customer" | "supplier" | "distributor";

export type AttributeTemplateSummarySchema = {
  template_id: UUID;
  template_name: string;
  display_name: string | null;
  description: string | null;
  is_frozen: boolean;
  tenant_id: UUID;
  number_of_archived_products: number;
  number_of_products: number;
  layout: TemplateTabData[];
  views: { id: string; name: string }[];
  forms: TemplateProductFormSchema[];
  enable_variant_products: boolean;
  enable_custom_forms: boolean;
  template_type: TemplateType | null;
};

export type AttributeTemplateSchema = AttributeTemplateSummarySchema & {
  groups: AttributeGroupSummary[];
  collections: CollectionSchema[];
  show_total_completion_score: boolean;
  show_mandatory_completion_score: boolean;
  show_related_entities: boolean;
  show_schema_tag: boolean;
  enable_company_hierarchy: boolean;
  schema_tags: {
    tenant_id: UUID;
    tag_name_attribute: AttributeSchema | null;
    tag_description_attribute: AttributeSchema | null;
  } | null;
};

export interface TemplateTabItem {
  id: string;
  display_name: string;
  name: string;
  type: "collection" | "group" | "company_hierarchy";
  attribute_count: number;
  disableReorder?: boolean;
  is_visible?: boolean;
  is_editable?: boolean;
  attributes: {
    name: string;
    display_name: string;
    type: string;
    object_type?: AttributeObjectType | null;
    is_visible: boolean;
    is_editable: boolean;
    editable_locked_reason?: EditBlockedReason;
    id: string;
    is_restricted: boolean;
  }[];
}

export type TemplateTabData = {
  id: string;
  display_name: string;
  name: string;
  tab_type:
    | "documents"
    | "overview"
    | "sku"
    | "seo"
    | "custom"
    | "change_request_form"
    | "company_hierarchy";
  is_enabled: boolean;
  priority: number;
  items: TemplateTabItem[];
};

export type TemplateProductFormSchema = {
  description: string | null;
  id: string;
  name: string;
  display_name: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
  steps: TemplateProductFormStepSchema[];
  template_id: string;
  storefront_id: string;
};

export type TemplateProductFormStepSchema = {
  name: string;
  id: string;
  submitted?: boolean;
  is_document_step?: boolean;
  items?: TemplateProductFormStepItemSchema[];
  categories?: {
    name: string;
    id: string;
  }[];
};
export type TemplateProductFormStepItemAttributeSchema = {
  attribute_id: string;
  name: string;
  display_name: string;
  id: string;
  is_required: boolean;
  priority: number;
  input_type: AttributeInput;
  description?: string;
  choices?: string[];
  object_type?: AttributeObjectType;
  target_object_type?: string;
  is_multiple_choice?: boolean;
  values?: AttributeValue[];
};
export type TemplateProductFormStepItemSchema = {
  id: string;
  type?: string;
  group_or_collection: string;
  group_or_collection_id: string;
  restrict_items_count: boolean;
  item_count: number;
  attributes: TemplateProductFormStepItemAttributeSchema[];
  columns?: AttributeSchema[];
  rows?: CollectionRowSchema[];
  value_rows?: CollectionValueRowSchema[];
};

/**
 * Product Creation Request - POST request body
 * @see /api/v2/storefronts/{storefront_id}/pim/product-creation-form
 */
export type ProductCreationRequest = {
  template_id: UUID;
  form_id: UUID;
  product_name: string;
  recipient_type: "internal" | "external";
  user_id?: UUID;
  name?: string;
  email?: string;
  message?: string;
};

export type ProductFormSchema = {
  number: string;
  created_at: string;
  country: string;
  form: TemplateProductFormSchema;
  id: string;
  form_id: string;
  // The live form this request was submitted from (`form_id` is its frozen copy).
  source_form_id: string;
  product_name: string;
  product_id: string;
  new_product_id: string;
  product_number: string;
  supplier_code: string;
  supplier_id: string | null;
  supplier_name: string;
  template: { template_id: string; template_name: string };
  status: "pending" | "submitted" | "completed" | "discarded";
  name: string;
  email: string;
  user: {
    id: string;
    firstname: string;
    lastname: string;
    email_address: string;
    username: string;
  };
  created_by_user: {
    id: string;
    firstname: string;
    lastname: string;
    email_address: string;
    username: string;
  };
  filled_steps: {
    priority: number;
    name: string;
    id: string;
  }[];
};

/**
 * Lightweight dynamic-only state for an in-progress product creation request.
 * Returned by GET .../pim/product-creation-form/{id}/state — used to refresh
 * saved values after a step save without re-fetching the (static) form tree.
 * `attribute_values` is keyed by attribute_id, `value_rows` by collection_id.
 * @see ProductFormSchema (the full form GET response this merges into)
 */
export type ProductCreationRequestStateSchema = {
  id: string;
  product_id: string | null;
  status: ProductFormSchema["status"];
  number: string | null;
  product_number: string | null;
  country: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  supplier_id: string | null;
  filled_steps: ProductFormSchema["filled_steps"];
  created_by_user: ProductFormSchema["created_by_user"] | null;
  user: ProductFormSchema["user"] | null;
  attribute_values: Record<string, AttributeValue[]>;
  value_rows: Record<string, CollectionValueRowSchema[]>;
};

export type TemplateViewSchema = {
  name: string;
  configurations: TemplateViewConfigurstions;
  id: string;
  layout: TemplateTabData[];
};

export type TemplateViewConfigurstions = {
  name: string;
  assigned_roles: {
    id: string;
    name: string;
  }[];
  is_assigned_to_customer: boolean;
  is_assigned_to_distributor: boolean;
  is_assigned_to_outside_login: boolean;
};

/**
 * POST /v2/tenants/{id}/pim/templates
 * @see /server/src/agilis/docs/odm/AttributeTemplateCreationSchema.yaml
 */
export type AttibuteTemplateCreationSchema = {
  name: string;
  template_id?: UUID;
  display_name?: string;
  description?: string;
  groups: { group_id: UUID; display_name?: string };
};

/**
 * PATCH /v2/tenants/{id}/pim/templates/{id}
 * @see /server/src/agilis/docs/odm/AttributeTemplatePatchSchema.yaml
 */
export type AttributeTemplatePatchSchema = {
  name: string;
  display_name?: string;
  description?: string;
  groups: { group_id: UUID; display_name?: string };
  groups_to_delete: UUID[];
};

export type ProductAttributesSummary = {
  product_name: string;
  product_id: string;
  product_slug: string;
  attributes: AttributeSchema[];
};

export type PIMProductCart = {
  id: UUID;
  name: string;
  produced_by: string;
  brand_name: string;
  description: string | null;
  slug: string;
  pim_product_type: "master" | "variant" | "standard";
  allow_custom_sku: boolean;
  product_number: string;
  product_skus: ProductSKU[];
  identifiers: Identifiers;
};

export type PIMProductBase = {
  description: string | null;
  name: string; // Also an attribute
  id: UUID;
  team_names: string[];
  is_accessible_by_all_customers_and_distributors?: boolean;
  // TODO determine what type to use here. Do not merge this as is
  produced_by: string;
  is_active: boolean;
  last_modified_full: { modified_at: string; modified_by: string | null };
  modified_at: string;
  status: ProductStatusType;
  display_status: ProductStatusType;
  image_url: string | null;
  published_product_id: UUID;
  primary_staged_product_id: UUID;
  slug: string;
  allow_custom_sku: boolean;
  display_completion_score: number;
  completion_score: number;
  chemical_name: string | null;
  product_number: string | null;
  product_owner: {
    id: UUID;
    firstname: string;
    lastname: string;
  } | null;
  mandatory_completion_score: number | null;
  display_mandatory_completion_score: number | null;
  show_mandatory_completion_score: boolean;
  show_total_completion_score: boolean;
  product_skus: ProductSKU[];
  product_applications: {
    filter_id: UUID;
    id: UUID;
    image_url: string | null;
    name: string;
  }[];
  /**
   * This is NOT all of the attributes in the `identifiers` group. Its a
   * specific subset of special attributes that are frequently displayed on the UI.
   */
  identifiers: Identifiers;
  safety_datasheet: string | null;
  technical_datasheet: string | null;
  pim_product_type: "master" | "variant" | "standard";
  master_product: {
    id: UUID;
    product_number: string;
    name: string;
  } | null;
};

export type ListPriceVisibility =
  | "available_everywhere"
  | "internal_and_customers"
  | "internal_users";

export type PIMProduct = PIMProductBase & {
  product_schema: {
    is_frozen: boolean;
    collections: CollectionSchema[];
    description: string | null;
    display_name: string;
    show_mandatory_completion_score: boolean;
    show_total_completion_score: boolean;
    show_schema_tag: boolean;
    schema_tags: {
      tenant_id: UUID;
      tag_name_attribute: AttributeSchema | null;
      tag_description_attribute: AttributeSchema | null;
    } | null;
    template_id: UUID;
    template_name: string;
    groups: AttributeGroupSummary[]; // This type might be wrong
    tenant_id: UUID;
    layout: TemplateTabData[];
  };
  assets: never[];
  is_editable: boolean;
  is_locked: boolean; // True when product is locked during approval workflow
  is_viewable: boolean; // This controls view inside login
  is_publishable: boolean;
  cover_image: Assets;
  number_of_list_prices: number;
  list_price_visibility: ListPriceVisibility;
  completion_score: number;
  display_completion_score: number;
  mandatory_completion_score: number | null;
  display_mandatory_completion_score: number | null;
  attribute_completion_values: {
    filled_attribute_count: number;
    filled_mandatory_attribute_count: number;
    total_mandatory_attribute_count: number;
    total_attribute_count: number;
  };
  non_team_permission: TeamPermissionsEnum;
  barentz_api_batches: BarentzIntegrationMasterDataExportBatches[];
};

export type BarentzIntegrationMasterDataExportBatches = {
  id: UUID;
  product_number: string;
  sku_number: string;
  status: APIStatusVariant;
  push_started: string;
  push_finished: string;
  response_status_code: number;
  response_error_message: string;
};

export type AssetsExternalLinks = {
  id: UUID;
  url: string;
  name: string;
  is_visible: boolean;
  product_ids: UUID[];
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
  number_of_products: number;
  number_of_profiles: number;
  is_editable: boolean;
  is_master: boolean;
};

export type PIMProductBasePaginagedOutput = WithPagination<{
  data: PIMProductBase[];
}>;

export type PIMProductPaginatedOutput = WithPagination<{
  data: PIMProduct[];
}>;

// Assets Types

export type PIMAssetsResponse = WithPagination<{
  data: Assets[];
}>;

export type SupportedAssetType = "video" | "image" | "document";
export type SupportedAssetAccessibility =
  | "public"
  | "logged_in_user"
  | "internal";
export type SupportedAssetCategoryType = string;

export type AssetsSummary = {
  id: UUID;
  name: string;
  file_name: string;
  asset_type: SupportedAssetType;
  asset_category: string;
  category: { id: string; name: SupportedAssetCategoryType };
  host: { id: string; name: string } | null;
  asset_mode: Assets["asset_mode"];
  visibility: SupportedAssetAccessibility;
  language: SupportedLanguage;
  region: string;
  modified_at: string;
  modified_by: string;
  is_downloadable: boolean;
  is_cover_image: boolean;
  country: string;
  issue_date: string | null;
  expiration_date: string | null;
  renewal_date: string | null;
  number_of_products: number;
  number_of_profiles?: number;
  product_ids: string[];
  is_generated: boolean;
  signed_url: string;
  preview_url: string;
  content_type: string | null;
  customers: AccessibilityData[];
  roles: AccessibilityData[];
  users: AccessibilityData[];
  allow_all_customers: boolean;
  allow_all_distributors: boolean;
  allow_all_roles: boolean;
};

export type AccessibilityData = {
  id: string;
  tenant_id?: string;
  name?: string;
  tenant_name?: string;
  firstname?: string;
  lastname?: string;
  fullname?: string;
};

type AssetsBase = {
  id: UUID;
  name: string;
  file_name: string;
  asset_type: SupportedAssetType;
  asset_size: number;
  asset_category: string;
  document_type: string;
  category: { id: string; name: SupportedAssetCategoryType };
  host: { id: string; name: string } | null;
  language: SupportedLanguage;
  region: string;
  document_url: string;
  modified_at: string;
  modified_by: string;
  number_of_products: number;
  number_of_profiles?: number;
  number_of_attribute_values: number;
  is_downloadable: boolean;
  signed_url: string;
  preview_url: string;
  is_cover_image: boolean;
  product_ids: string[];
  profile_ids: Record<string, string[]>;
  content_type: string | null;
  issue_date: string | null;
  expiration_date: string | null;
  renewal_date: string | null;
  country: string;
  visibility: SupportedAssetAccessibility;
  is_master: boolean;
  role_ids: string[];
  user_ids: string[];
  customer_ids: string[];
  customers: AccessibilityData[];
  roles: AccessibilityData[];
  users: AccessibilityData[];
  allow_all_customers: boolean;
  allow_all_distributors: boolean;
  allow_all_roles: boolean;
  asset_mode: "gcs" | "external" | "cache";
};

type GeneratedAssets = AssetsBase & {
  is_generated: true;
  tds_generated_asset: TdsGeneratedAssetSchema;
};

type NonGeneratedAssets = AssetsBase & {
  is_generated: false;
};

export type Assets = GeneratedAssets | NonGeneratedAssets;
export interface PermissionObjectType {
  suppliers: string;
  customers: string;
  distributors: string;
  quote_requests: string;
  templates: string;
  leads: string;
  attributes: string;
  sample_requests: string;
  groups: string;
  products: string;
  lists: string;
  collections: string;
  assets: string;
  orders: string;
}

export type RoleSummary = {
  id: UUID;
  permission_object_type: PermissionObjectType;
  name: string;
}[];

export type Role = {
  id: UUID;
  name: "Admin" | "Product Manager" | "Sales Manager" | "Standard User";
  enable_product_type_visibility: boolean;
  permissions: {
    id: UUID;
    is_active: boolean;
    permission: {
      id: UUID;
      name: string;
      object_type: string;
      slug: Permission;
    };
  }[];
};

export type ProductStatusType =
  | "draft"
  | "published"
  | "staged"
  | "unpublished"
  | "unpublished_staged"
  | "archived"
  | "discarded"
  | "bulk_edit_in_progress"
  | "creation_requested";

export type ProductStatusesSchema = {
  statuses: ProductStatusType[];
};

export type ProductUploadStatus =
  | "successful"
  | "failed"
  | "in_progress"
  | "paused"
  | "cancelled";

export type PIMProductActivity = {
  id: string;
  target_id: string;
  modified_at: string;
  target_type: string;
  version_number: number;
  modified_by_id: string;
  created_at: string;
  status: string;
  last_modified_by: string;
};

export type PIMProductUploadSchema = {
  upload_number: string;
  id: string;
  template_name: string;
  created_at: string;
  created_by: string;
  status: ProductUploadStatus;
  upload_log_url?: string;
};

export type UploadNotification = {
  status: PIMProductUploadSchema["status"];
  number_of_product: number;
  upload_number: string;
};

export type TDSGeneratedAssetRequestSchema = {
  asset_type?: "document";
  asset_category?: "tds";
  category_id?: string;
  sections?:
    | {
        order: number;
        group_or_collection_id: UUID;
        is_full_width: boolean;
      }[]
    | null;
  language: SupportedLanguage;
  region?: string;
  footer_on_last_page_only?: boolean;
  is_active: boolean;
  title: string;
  letterhead_file_id?: UUID | null;
  footer_content?: string;
  footer_image_id?: UUID | null;
};

type TdsGeneratedAssetsSectionBase = {
  order: number;
};

type TdsGeneratedAssetsSectionCollection = TdsGeneratedAssetsSectionBase & {
  group_type: "collection";
  collection: {
    id: string;
    name: string;
    display_name: string;
    description: string;
  };
};

type TdsGeneratedAssetsSectionGroup = TdsGeneratedAssetsSectionBase & {
  group_type: "group";
  group: {
    id: string;
    name: string;
    display_name: string;
    description: string;
  };
};

export type TdsGeneratedAssetsSectionsSchema =
  | TdsGeneratedAssetsSectionCollection
  | TdsGeneratedAssetsSectionGroup;

export type TdsGeneratedAssetSchema = {
  id: UUID;
  asset_type: "document";
  asset_category: SupportedAssetCategoryType;
  sections: TdsGeneratedAssetsSectionsSchema[];
  is_active: boolean;
  language: SupportedLanguage;
  region?: string;
  footer_on_last_page_only: boolean;
  footer_content?: string;
  number_of_assets: number;
  title: string;
  letterhead_document?: TDSDocument;
  footer_document?: TDSDocument;
  category_id?: string;
};

export type TdsGenerateAssetJobSchema = {
  id: UUID;
  /** pending/in-progress from RQ; success/failure come from pdfkit mid-flight */
  status: "pending" | "failed" | "failure" | "finished" | "success" | string;
  tenant_id?: UUID;
  product_id?: UUID;
  tds_generated_asset_id?: UUID;
  tds_asset?: Assets;
};

export type UploadedAssetSchema = {
  id: UUID;
  asset_type: SupportedAssetType;
  category: { name: SupportedAssetCategoryType; id: UUID };
  language: SupportedLanguage;
  number_of_assets: number;
  modified_by: string;
  modified_at: string;
  upload_status: "pending" | "processing" | "completed" | "failed";
  result_csv_blob_name?: string;
  assets: {
    name: string;
    asset_type: Assets["asset_type"];
    category: Assets["asset_category"];
  }[];
  error_details?: string;
};

export type UploadAssetRequestSchema = {
  asset_type: SupportedAssetType;
  category_id: UUID;
  language: SupportedLanguage;
  files: FormData;
};

export type UploadedAssetInProgressSchema = {
  id: UUID;
  file_name: string;
  asset_name: string;
  size: number;
  replace_asset_id: UUID | null;
  errors?: string;
};

export type UploadAssetsResponseSchema = {
  id: UUID;
  number: string;
  data: UploadedAssetInProgressSchema[];
  asset_type: SupportedAssetType;
  category: { name: SupportedAssetCategoryType; id: UUID };
  asset_host: { name: string; id: UUID } | null;
  asset_mode: Assets["asset_mode"];
  language: SupportedLanguage;
  number_of_prospected_assets: number;
  number_uploaded: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  error_csv_blob_name?: string;
  success_csv_blob_name?: string;
  replace_existing?: boolean;
};

export type PortfolioControlsSchema = {
  id: UUID;
  filter_type: string;
  attribute: AttributeSchemaBase;
  display_name: string;
  is_list: boolean;
  is_searchable: boolean;
  is_filterable: boolean;
};

export type ProductExportStatusSchema = {
  id: UUID;
  number: string;
  query_parameters: Record<string, number | string | Array<unknown>>;
  total_jobs: number;
  total_products: number;
  status: ProductUploadStatus;
  documents: ProductSelectorCSVDocument[];
};

export type GridItemProps = {
  can_edit: boolean;
  can_change_visibility: boolean;
  can_delete: boolean;
  can_external_download: boolean;
  can_view_details: boolean;
};

export type ProductListPriceSchema = {
  id: string;
  number: string;
  sku_number: string;
  sku: ProductSKU | null;
  sku_id: string | null;
  minimum_units: number;
  quantity?: number;
  price: string;
  currency?: CurrencyCode;
  valid_until: string;
};

export type PricingPriceTierSchema = {
  product_sku: ProductSKU;
  price_tiers: IPriceTier[];
};

export type ProductListPriceUpdateSchema = {
  valid_until: string;
  minimum_units: number;
  price: string;
  currency?: CurrencyCode;
  quantity?: number;
};

// Minimal product types for the new list endpoint
export type PIMProductMinimalLayoutItem = {
  id: string;
  name: string;
  display_name: string;
  type: "group" | "collection";
  group_id: string;
  attribute_count: number;
  attributes: any[]; // Always empty array in minimal response
};

export type PIMProductMinimalLayoutTab = {
  id: string;
  name: string;
  display_name: string;
  tab_type: "overview" | "custom" | "sku" | "documents" | "seo";
  is_enabled: boolean;
  priority: number;
  items: PIMProductMinimalLayoutItem[];
};

export type CardDataField = {
  display_name: string;
  value: string | null;
};

export type CardData = {
  brand_name: CardDataField;
  cas_number: CardDataField;
  chemical_name: CardDataField;
  product_description: CardDataField;
  product_line: CardDataField;
};

export type SimpleAsset = {
  cdn_url: string | null;
  file_name: string;
  id: string;
  name: string;
  number: string;
  preview_url: string;
  signed_url: string;
  size: number;
};

export type PIMProductMinimal = {
  id: string;
  name: string | null;
  product_number: string;
  slug: string | null;
  completion_score: number;
  mandatory_completion_score: number;
  display_completion_score?: number;
  display_mandatory_completion_score?: number;
  cover_image: string | null;
  display_status: ProductStatusType;
  status: ProductStatusType;
  last_modified_full: {
    modified_at: string;
    modified_by: string;
  } | null;
  card_data: CardData | null;
  show_total_completion_score?: boolean;
  show_mandatory_completion_score?: boolean;
  sds_assets: SimpleAsset[];
  tds_assets: SimpleAsset[];
  has_multiple_documents?: boolean;
  has_sample_sku?: boolean;
  pim_product_type?: "master" | "variant" | "standard";
};

export type PIMProductMinimalPaginatedOutput = WithPagination<{
  data: PIMProductMinimal[];
  /**
   * AP-1016 (Advanced Filters, Part 2) — see `AdvancedFiltersAppliedCard` in
   * types.ts. UNCONFIRMED for this endpoint: this type backs
   * `v2_storefronts_id_pim_products_card` (`pim/products-card`), the
   * endpoint the grid view and all public/buyer/portfolio listing surfaces
   * use — but the BE's Part 2 integration reference only confirms
   * `advanced_filters` request/response support on 3 tenant-scoped
   * endpoints (`pim/products-list-minimal`, `pim/products`,
   * `pim/products/summary`); `products-card` isn't in that list. Do not
   * send `advanced_filters` on this endpoint or rely on this field until
   * confirmed with BE.
   */
  advanced_filters_applied?: AdvancedFiltersAppliedCard[];
}>;

export type ReferenceEntitySchema = {
  description: string;
  display_name: string;
  id: string;
  name: string;
  row_count: number;
  columns: {
    attribute_id: UUID;
    attribute_name: string;
    display_name: string;
    id: string;
    input_type: AttributeInput;
    position: number;
  }[];
};

export type ReferenceEntityMappedAttribute = {
  attribute_display_name: string;
  attribute_id: UUID;
  attribute_input_type: string;
  attribute_name: string;
  column_display_name: string;
  column_id: UUID;
  column_name: string;
  column_position: number;
};

export type ReferenceEntityMappingSchema = {
  context_id: UUID;
  context_name: string;
  context_type: "group" | "collection";
  feature_enabled: boolean;
  has_mapping: boolean;
  mappings: {
    mapped_attributes: ReferenceEntityMappedAttribute[];
    mapped_attributes_count: number;
    mapping: { created_at: string; id: string };
    reference_entity: ReferenceEntitySchema;
  }[];
  mappings_count: number;
};

type AutoFillSchema = {
  attribute_id: UUID;
  attribute_name: string;
  is_bridge: boolean;
  iteration: number;
  value_display: string;
  value_id: UUID;
};

export type ReferenceEntityConstraintSchema = {
  is_valid_combination: boolean;
  matching_rows_count: number;
  reference_entity_id: UUID;
  reference_entity_name: string;
  total_rows_count: number;
  auto_fills: AutoFillSchema[];
  constraints: {
    action: "auto_fill" | "filter" | "filter_and_clear" | "clear" | "no_change";
    attribute_display_name: string;
    attribute_id: UUID;
    attribute_name: string;
    column_id: UUID;
    constrained_by_entities: { id: UUID; name: string }[];
    constraint_count: number;
    valid_values: { display: string; id: string }[];
    valid_values_count: number;
    value_to_set?: string;
    value_to_set_display?: string;
  }[];
};
/**
 * Reference Entity from backend API
 * @see /api/v2/storefronts/{storefront_id}/reference-entities
 */
export type ReferenceEntity = {
  id: UUID;
  name: string;
  display_name: string;
  description: string;
  column_count: number;
  row_count: number;
  created_at: string;
};

export type PaginatedReferenceEntities = WithPagination<{
  data: ReferenceEntity[];
}>;

/**
 * Reference Entity Detail - Column definition
 * @see /api/v2/storefronts/{storefront_id}/reference-entities/{id}/data
 */
export type ReferenceEntityColumn = {
  id: UUID;
  attribute_id: UUID;
  attribute_name: string;
  display_name: string;
  position: number;
  input_type: string;
};

/**
 * Reference Entity Detail - Row cell value
 */
export type ReferenceEntityCellValue = {
  column_id: UUID;
  value_id: UUID;
  value_display: string;
};

/**
 * Reference Entity Detail - Row data (dynamic keys based on columns)
 */
export type ReferenceEntityRow = {
  id: UUID;
  position: number;
  [key: string]: UUID | number | ReferenceEntityCellValue;
};

/**
 * Reference Entity Detail - Full response
 */
export type ReferenceEntityDetailResponse = {
  entity: {
    id: UUID;
    name: string;
    display_name: string;
    description: string;
  };
  columns: ReferenceEntityColumn[];
  data: ReferenceEntityRow[];
  pagination: {
    offset: number;
    limit: number;
    order_by: string;
    total: number;
  };
};

export type CollectionObjectType =
  | "skus"
  | "properties"
  | "specifications"
  | "applications"
  | "value_propositions"
  | "functions"
  | "industries"
  | "market_segments";

/**
 * Tenant PIM PATCH/PUT responses that return a minimal `product_schema` slice
 * (collections and/or groups) for optimistic UI before a full product GET.
 */
export type PIMProductPartial = Pick<
  PIMProduct,
  | "id"
  | "name"
  | "display_status"
  | "product_number"
  | "primary_staged_product_id"
> & {
  product_schema?: {
    collections?: Array<{
      id: string;
      value_rows?: CollectionValueRowSchema[];
      columns?: AttributeSchema[];
    }>;
    groups?: Array<{
      id: string;
      attributes: AttributeSchema[];
    }>;
  };
};

export type PIMProductStatusAck = {
  id: string;
  status: string;
  display_status: string;
  product_number: string | null;
  primary_staged_product_id: string | null;
};
